/**
 * VoiceChatBase - Abstract base class for voice chat implementations
 * 
 * This class provides direct implementation of voice chat functionality,
 * closely matching the original useOpenAIStore implementation.
 */
import { ref, type Ref } from 'vue';
import { toolRegistry, type Message, type FunctionCallPayload, safeJsonParse, type ToolOverridesMap } from '../tools/ToolRegistry';
import { useLogger } from '~/composables/useLogger';
import { useApiService } from '~/composables/useApiService';
import { useOpenAIService, type SessionConfig } from '~/composables/useOpenAIService';
import { MicrophonePermissionService } from '~/plugins/microphone-permission';
import { Capacitor } from '@capacitor/core';
import { usePostHog, USAGE_EVENTS } from '~/composables/usePostHog';
import { useFirebaseAnalytics } from '~/composables/useFirebaseAnalytics';
import { CHAT_VERSION } from '~/src/constants';

// Interface for WebRTC session data
interface SessionData {
  url: string;
  token: string;
}

// Unified function call state interface
interface FunctionCallState {
  id: string;
  name: string;
  arguments: string;
  status: 'created' | 'accumulating' | 'complete' | 'executing' | 'executed' | 'error';
  createdAt: number;
  result?: any;
  error?: string;
}

// Tool context interface
interface ToolContext {
  callId: string;
  dataChannel: RTCDataChannel;
  completedCalls: Set<string>;
  logger: any;
  conversation: Message[];
}

export interface VoiceChatOptions {
  /**
   * Tool overrides to customize tool descriptions, continuation instructions, 
   * parameter descriptions, and parameter examples.
   */
  toolOverrides?: ToolOverridesMap;
  
  /**
   * Voice to use for text-to-speech
   * Defaults to 'alloy'
   */
  voice?: string;
  
  /**
   * Inactivity timeout in milliseconds
   * The chat will automatically end if there's no activity for this duration
   * Defaults to 15000 (15 seconds)
   */
  inactivityTimeoutMs?: number;

  /**
   * Implementation type identifier for logging
   * Helps distinguish between different implementations (e.g., 'senior', 'full')
   * Defaults to 'base'
   */
  implementationType?: string;
}

export abstract class VoiceChatBase {
  // Common reactive state
  public isSessionActive: Ref<boolean>;
  public isConnecting: Ref<boolean>;
  public status: Ref<string>;
  public hasError: Ref<boolean>;
  public errorMessage: Ref<string>;
  public conversation: Ref<Message[]>;
  public isShuttingDown: Ref<boolean>;
  
  // WebRTC state
  private audioStream: Ref<MediaStream | null>;
  private peerConnection: Ref<RTCPeerConnection | null>;
  private dataChannel: Ref<RTCDataChannel | null>;
  private ephemeralUserMessageId: Ref<string | null>;
  private functionCalls: Ref<Map<string, FunctionCallState>>;
  private voice: Ref<string>;
  
  // Tool overrides for this instance
  protected toolOverrides: ToolOverridesMap;
  
  // Implementation type for logging
  protected implementationType: string;
  
  // Audio element reference
  private audioEl: HTMLAudioElement | null = null;
  
  // Native microphone permission service
  private microphonePermissionService: MicrophonePermissionService;
  
  // Analytics
  private analytics;
  private firebaseAnalytics;
  private sessionStartTime: number | null = null;
  
  // Inactivity timer
  private inactivityTimerId: number | null = null;
  private inactivityTimeoutMs: number;
  
  // Audio activity tracking
  private isAudioPlaying: Ref<boolean> = ref(false);
  private audioActivityHeartbeatId: number | null = null;
  
  // Add a timestamp field for audio activity tracking
  private lastAudioActivityTime: number = 0;
  
  constructor(options: VoiceChatOptions = {}) {
    // Initialize reactive state
    this.isSessionActive = ref(false);
    this.isConnecting = ref(false);
    this.status = ref('Ready');
    this.hasError = ref(false);
    this.errorMessage = ref('');
    this.conversation = ref([]);
    this.isShuttingDown = ref(false);
    
    // WebRTC state
    this.audioStream = ref(null);
    this.peerConnection = ref(null);
    this.dataChannel = ref(null);
    this.ephemeralUserMessageId = ref(null);
    this.functionCalls = ref(new Map());
    this.voice = ref(options.voice || 'alloy');
    
    // Set inactivity timeout from options or use default
    this.inactivityTimeoutMs = options.inactivityTimeoutMs || 15000;
    
    // Store tool overrides
    this.toolOverrides = options.toolOverrides || {};
    
    // Initialize implementation type
    this.implementationType = options.implementationType || 'base';
    
    // Apply tool overrides if provided
    if (Object.keys(this.toolOverrides).length > 0) {
      toolRegistry.setToolOverrides(this.toolOverrides);
    }
    
    // Initialize microphone permission service
    this.microphonePermissionService = MicrophonePermissionService.getInstance();
    
    // Initialize analytics
    this.analytics = usePostHog();
    this.firebaseAnalytics = useFirebaseAnalytics();
    
    const logger = useLogger();
    logger.info(`VoiceChat_${this.implementationType}: Initialized voice chat instance`);
  }
  
  /**
   * Set tool overrides for this voice chat instance
   * Will be applied when tools are registered
   */
  public setToolOverrides(overrides: ToolOverridesMap): void {
    this.toolOverrides = overrides;
    toolRegistry.setToolOverrides(overrides);
  }
  
  /**
   * Get current tool overrides
   */
  public getToolOverrides(): ToolOverridesMap {
    return this.toolOverrides;
  }
  
  /**
   * Start a voice chat session
   * This method initializes the session, registers tools, and establishes connections
   */
  public async startSession(): Promise<void> {
    const logger = useLogger();
    
    if (this.isSessionActive.value || this.isConnecting.value) {
      logger.info(`VoiceChat_${this.implementationType}: Session already active or connecting`);
      return;
    }
    
    this.isConnecting.value = true;
    this.status.value = 'Connecting...';
    this.conversation.value = [];
    this.hasError.value = false;
    
    try {
      // Start session tracking
      this.sessionStartTime = Date.now();
      await this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_SESSION_START}_${this.implementationType.toLowerCase()}`, 
         {chatVersion: CHAT_VERSION}
      );
      await this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_SESSION_START}_${this.implementationType.toLowerCase()}`, {
        chatVersion: CHAT_VERSION
      });
      
      // Apply any stored tool overrides before registering tools
      if (Object.keys(this.toolOverrides).length > 0) {
        toolRegistry.setToolOverrides(this.toolOverrides);
      }
      
      // 1. Register appropriate tools for this implementation
      await this.registerTools();
      
      // 2. Check and request microphone permissions (with special handling for Android)
      await this.ensureMicrophonePermissions();
      
      // 3. Initialize session with OpenAI
      logger.info(`VoiceChat_${this.implementationType}: Initializing session`);
      
      try {
        // Get session token using the new service with configuration
        const openAIService = useOpenAIService();
        const sessionConfig: SessionConfig = {
          model: "gpt-4o-realtime-preview-2024-12-17",
          voice: this.voice.value,
          modalities: ["audio", "text"],
          tool_choice: "auto",
          instructions: this.getInitialPrompt(),
          user_id: "dummy-user-id" // can be used later if important to the server call
        };
        
        const data = await openAIService.createSession(sessionConfig);
        
        logger.info(`VoiceChat_${this.implementationType}: Session data:`,
          data ? `URL: ${data.url ? 'exists' : 'missing'}, Token: ${data.token ? 'exists' : 'missing'}` : 'null');
        
        // Set up WebRTC connection
        await this.setupWebRTCConnection(data.url, data.token);
        this.isSessionActive.value = true;
        this.status.value = 'Connected';
      } catch (apiError: any) {
        logger.error(`VoiceChat_${this.implementationType}: API call failed:`, apiError);
        this.showError(apiError.message);
        this.status.value = 'Error connecting';
        throw apiError;
      }
    } catch (error: any) {
      logger.error(`VoiceChat_${this.implementationType}: Session start error:`, error);
      this.showError(`Failed to start voice chat: ${error.message || 'Unknown error'}`);
      this.isSessionActive.value = false;
      this.status.value = 'Error';
      const errorData = {
        error: error instanceof Error ? error.message : String(error),
        context: 'session_start'
      };
      await this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_ERROR}_${this.implementationType.toLowerCase()}`, errorData);
      await this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_ERROR}_${this.implementationType.toLowerCase()}`, errorData);
      throw error;
    } finally {
      this.isConnecting.value = false;
    }
  }
  
  /**
   * Ensure microphone permissions are granted
   * Uses native permission handling on Android
   */
  private async ensureMicrophonePermissions(): Promise<void> {
    const logger = useLogger();
    logger.info('VoiceChatBase: Checking microphone permissions');
    
    // Handle Android differently using our native plugin
    if (Capacitor.getPlatform() === 'android') {
      logger.info('VoiceChatBase: Using native Android permission handling');
      
      try {
        // First verify if the plugin is registered and working
        if (!this.microphonePermissionService) {
          throw new Error('Microphone permission service is not initialized');
        }
        
        logger.info('VoiceChatBase: Calling ensureMicrophoneReady');
        const isReady = await this.microphonePermissionService.ensureMicrophoneReady()
          .catch(error => {
            logger.error('VoiceChatBase: Error in ensureMicrophoneReady:', error);
            throw new Error(`Microphone permission error: ${error.message || 'Unknown error'}`);
          });
        
        logger.info('VoiceChatBase: Microphone ready status:', isReady);
        
        if (!isReady) {
          // Get detailed information for debugging
          try {
            logger.info('VoiceChatBase: Getting microphone info for diagnostics');
            const micInfo = await this.microphonePermissionService.getMicrophoneInfo();
            logger.info('VoiceChatBase: Android microphone status', micInfo);
            
            // Detailed error message based on state
            if (micInfo.micMuted) {
              throw new Error('Microphone is muted. Please unmute your device microphone.');
            } else if (micInfo.devices && micInfo.devices.length === 0) {
              throw new Error('No microphone hardware detected on your device.');
            } else {
              // Try to optimize audio settings as a last resort
              logger.info('VoiceChatBase: Trying audio mode optimization');
              await this.microphonePermissionService.setupAudioMode();
              
              // Final check
              const finalPermissionCheck = await this.microphonePermissionService.checkPermission(true);
              
              if (!finalPermissionCheck.granted) {
                throw new Error('Microphone permission denied. Please allow microphone access in your device settings.');
              } else if (!finalPermissionCheck.available) {
                throw new Error('Microphone permission granted but hardware access failed. Please check if another app is using your microphone.');
              }
            }
          } catch (infoError) {
            logger.error('VoiceChatBase: Error getting microphone diagnostic info:', infoError);
            throw new Error('Could not access microphone. Please check your device settings and ensure microphone access is enabled.');
          }
        }
        
        // If we made it here, we should be good to go on Android
        logger.info('VoiceChatBase: Android microphone permission and access confirmed');
      } catch (error) {
        logger.error('VoiceChatBase: Android permission handling error:', error);
        
        // Try to open settings if we get a permission-related error
        try {
          logger.info('VoiceChatBase: Attempting to open settings for manual permission grant');
          await this.microphonePermissionService.openSettings();
          throw new Error('Please grant microphone permission in your device settings, then try again.');
        } catch (settingsError) {
          logger.error('VoiceChatBase: Error opening settings:', settingsError);
          throw new Error('Microphone access denied. Please enable microphone permission in your device settings and restart the app.');
        }
      }
    } else {
      // Standard web permission flow
      logger.info('VoiceChatBase: Using standard web permission flow');
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Release the stream immediately, we just needed to trigger the permission prompt
        stream.getTracks().forEach(track => track.stop());
        logger.info('VoiceChatBase: Microphone permission granted');
      } catch (err: any) {
        logger.error('VoiceChatBase: Microphone permission error:', err);
        
        // Provide more user-friendly error messages
        if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
          throw new Error('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          throw new Error('No microphone found. Please check your microphone connection.');
        } else {
          throw new Error(`Microphone error: ${err.message || 'Unknown error'}`);
        }
      }
    }
  }
  
  /**
   * Stop the current voice chat session
   */
  public stopSession(): Promise<void> {
    return this._stopSession();
  }
  
  /**
   * Internal implementation of stop session with optional disablePermissionReset
   */
  private async _stopSession(disablePermissionReset: boolean = false): Promise<void> {
    const logger = useLogger();
    
    // Clear inactivity timer when stopping session
    this.clearInactivityTimer();
    
    if (!this.isSessionActive.value && !this.isShuttingDown.value) {
      return;
    }
    
    logger.info(`VoiceChat_${this.implementationType}: Stopping session`);
    this.isSessionActive.value = false;
    this.isConnecting.value = false;
    
    try {
      // Track session end with duration
      if (this.sessionStartTime) {
        const sessionData = {
          duration_ms: Date.now() - this.sessionStartTime,
          message_count: this.conversation.value.length
        };
        await this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_SESSION_END}_${this.implementationType.toLowerCase()}`, sessionData);
        await this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_SESSION_END}_${this.implementationType.toLowerCase()}`, sessionData);
        this.sessionStartTime = null;
      }
      
      // Close data channel
      if (this.dataChannel.value) {
        logger.info(`VoiceChat_${this.implementationType}: Closing data channel`);
        this.dataChannel.value.close();
        this.dataChannel.value = null;
      }
      
      // Close peer connection
      if (this.peerConnection.value) {
        logger.info(`VoiceChat_${this.implementationType}: Closing peer connection`);
        this.peerConnection.value.close();
        this.peerConnection.value = null;
      }
      
      // Stop audio tracks
      if (this.audioStream.value) {
        logger.info(`VoiceChat_${this.implementationType}: Stopping audio tracks`);
        this.audioStream.value.getTracks().forEach(track => track.stop());
        this.audioStream.value = null;
      }
      
      // Clean up session resources
      this.cleanupSession();
      
      // Clear permission cache on Android
      if (Capacitor.getPlatform() === 'android' && !disablePermissionReset) {
        this.microphonePermissionService.clearCache();
      }
      
      this.status.value = 'Ready';
      this.ephemeralUserMessageId.value = null;
      logger.info(`VoiceChat_${this.implementationType}: Session stopped`);
    } catch (error) {
      this.showError('Error stopping session', error);
      const errorData = {
        error: error instanceof Error ? error.message : String(error),
        context: 'session_stop'
      };
      await this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_ERROR}_${this.implementationType.toLowerCase()}`, errorData);
      await this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_ERROR}_${this.implementationType.toLowerCase()}`, errorData);
    } finally {
      // Make sure to reset the shutting down state regardless of success or failure
      this.isShuttingDown.value = false;
    }
  }
  
  /**
   * Extract properly structured data from various response formats
   */
  private extractRealTimeData(data: any): SessionData {
    const logger = useLogger();
    
    // If data already has url and token, use it directly
    if (data && data.url && data.token) {
      return data;
    }
    
    // Look for common wrappers
    if (data) {
      if (data.body && typeof data.body === 'object') {
        logger.info('Extracted data from body property');
        return data.body;
      }
      if (data.data && typeof data.data === 'object') {
        logger.info('Extracted data from data property');
        return data.data;
      }
      if (data.statusCode && data.body) {
        // It might be a standard Nuxt API response
        try {
          const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
          logger.info('Extracted data from Nuxt response body');
          return bodyData;
        } catch (e) {
          logger.error('Failed to parse Nuxt response body', e);
        }
      }
    }
    
    // Return original data if no extraction was possible
    return data;
  }
  
  /**
   * Set up WebRTC connection with the provided URL and token
   */
  private async setupWebRTCConnection(url: string, token: string): Promise<void> {
    const logger = useLogger();
    
    if (!url || !token) {
      throw new Error('Missing URL or token');
    }
    
    logger.info(`VoiceChat_${this.implementationType}: Setting up WebRTC connection...`);
    
    try {
      // 1. Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      this.peerConnection.value = pc;
      
      // Setup audio element - make sure it's hidden
      if (this.audioEl) {
        this.audioEl.remove();
      }
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.style.display = 'none';
      audio.style.width = '0';
      audio.style.height = '0';
      audio.style.position = 'absolute';
      audio.style.left = '-9999px';
      
      // Add audio event listeners to monitor playback
      audio.onplaying = () => {
        logger.info('VoiceChatBase: Audio started playing');
        this.isAudioPlaying.value = true;
        this.lastAudioActivityTime = Date.now();
        
        this.startAudioActivityHeartbeat();
        this.resetInactivityTimer();
      };
      
      audio.onpause = audio.onended = () => {
        logger.info('VoiceChatBase: Audio stopped playing');
        this.isAudioPlaying.value = false;
        this.stopAudioActivityHeartbeat();
      };
      
      // Limit timeupdate frequency to avoid excessive resets
      let lastTimeUpdateReset = 0;
      let lastTimeValue = 0;
      
      audio.ontimeupdate = () => {
        const now = Date.now();
        // Detect real progress in audio playback
        const hasProgressedInPlayback = Math.abs(audio.currentTime - lastTimeValue) > 0.1;
        
        // Only process timeupdate when audio has actually progressed
        if (now - lastTimeUpdateReset > 3000 && hasProgressedInPlayback) {
          lastTimeUpdateReset = now;
          lastTimeValue = audio.currentTime;
          
          // Only reset timer if we have confirmation audio is actually playing
          if (this.isAudioPlaying.value && 
              !audio.paused && 
              audio.currentTime > 0 &&
              hasProgressedInPlayback) {
            
            // logger.debug(`VoiceChatBase: Audio timeupdate with progress at ${audio.currentTime.toFixed(2)}`);
            this.lastAudioActivityTime = now;
            this.resetInactivityTimer();
          }
        }
      };
      
      // Add stalled and waiting handlers to ensure we don't timeout during buffering
      audio.onstalled = audio.onwaiting = () => {
        if (this.isAudioPlaying.value) {
          logger.info('VoiceChatBase: Audio buffering - resetting timer');
          this.lastAudioActivityTime = Date.now();
          this.resetInactivityTimer();
        }
      };
      
      document.body.appendChild(audio);
      this.audioEl = audio;
      
      // 2. Set up data channel
      const dc = pc.createDataChannel('chat');
      this.dataChannel.value = dc;
      
      dc.onopen = () => {
        logger.info('VoiceChatBase: Data channel opened');
        
        // Send session.update to configure the tools
        const toolsConfig = toolRegistry.getAllToolSchemas();
        
        logger.info('VoiceChatBase: Preparing to send tool configuration');
        logger.info(`VoiceChatBase: Tool configuration summary: ${toolsConfig.length} tools defined`);
        
        // Build the full session update message
        const sessionUpdate = {
          type: 'session.update',
          session: {
            tools: toolsConfig,
            input_audio_transcription: {
              model: 'whisper-1',
            },
          },
        };
        
        // Send the session update
        dc.send(JSON.stringify(sessionUpdate));
        logger.info('VoiceChatBase: Session update sent with tools configuration');
        
        // Send response.create to trigger an initial greeting from the assistant
        setTimeout(() => {
          const initialPrompt = {
            type: 'response.create',
            response: {
              instructions: this.getInitialPrompt()
            }
          };
          dc.send(JSON.stringify(initialPrompt));
          logger.info('VoiceChatBase: Sent initial prompt to trigger assistant greeting');
        }, 1000); // Short delay to ensure session update is processed first
      };
      
      dc.onmessage = this.handleDataChannelMessage.bind(this);
      
      // 3. Handle remote stream (AI voice)
      pc.ontrack = (event) => {
        logger.info('VoiceChatBase: Remote audio track received');
        if (this.audioEl) {
          this.audioEl.srcObject = event.streams[0];
        }
      };
      
      // Monitor connection state changes
      pc.oniceconnectionstatechange = () => {
        logger.info('VoiceChatBase: ICE connection state changed to:', pc.iceConnectionState);
        
        // Log detailed information for failure states
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          logger.error('VoiceChatBase: WebRTC connection problem', {
            iceState: pc.iceConnectionState,
            connectionState: pc.connectionState,
            signalingState: pc.signalingState,
            localDescription: pc.localDescription ? pc.localDescription.type : 'none',
            remoteDescription: pc.remoteDescription ? pc.remoteDescription.type : 'none',
            timestamp: new Date().toISOString()
          });
        }
      };
      
      // Monitor general connection state changes
      pc.onconnectionstatechange = () => {
        logger.info('VoiceChatBase: Connection state changed to:', pc.connectionState);
        
        if (pc.connectionState === 'failed') {
          logger.error('VoiceChatBase: WebRTC connection failed', {
            iceState: pc.iceConnectionState,
            signalingState: pc.signalingState,
            timestamp: new Date().toISOString()
          });
        }
      };
      
      // Add error event handlers for data channel
      dc.onerror = (error) => {
        logger.error('VoiceChatBase: Data channel error', {
          error: error.error?.message || 'Unknown data channel error',
          state: dc.readyState,
          timestamp: new Date().toISOString()
        });
      };
      
      // 4. Get user's microphone with platform-specific handling
      logger.info('VoiceChatBase: Requesting microphone access...');
      
      let stream: MediaStream;
      
      // Use optimized settings for Android
      if (Capacitor.getPlatform() === 'android') {
        logger.info('VoiceChatBase: Using optimized audio settings for Android');
        
        // Ensure audio mode is set up correctly
        await this.microphonePermissionService.setupAudioMode();
        
        // Use specific audio constraints for Android
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false
          }
        });
      } else {
        // Default settings for other platforms
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      
      this.audioStream.value = stream;
      logger.info('VoiceChatBase: Microphone access granted');
      
      // 5. Add audio track to peer connection
      stream.getAudioTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // 6. Create offer and set local description
      logger.info('VoiceChatBase: Creating and sending offer to OpenAI...');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      // 7. Send the SDP offer to OpenAI
      const model = "gpt-4o-realtime-preview-2024-12-17";
      const baseUrl = "https://api.openai.com/v1/realtime";
      const response = await fetch(`${baseUrl}?model=${model}&voice=${this.voice.value}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('VoiceChatBase: OpenAI API error:', errorText);
        throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
      }
      
      // 8. Set remote description
      logger.info('VoiceChatBase: Received answer from OpenAI');
      const answerSdp = await response.text();
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp
      });
      
      logger.info('VoiceChatBase: WebRTC connection established successfully');
      this.status.value = 'Connected';
      this.isSessionActive.value = true;
      
      // Start inactivity timer once connection is established
      this.startInactivityTimer();
    } catch (error: any) {
      logger.error('VoiceChatBase: Error setting up WebRTC connection:', error);
      
      // Special handling for permission errors
      if (error.name === 'NotAllowedError' || error.message?.includes('Permission')) {
        // On Android, open settings
        if (Capacitor.getPlatform() === 'android') {
          logger.info('VoiceChatBase: Opening Android settings for manual permission grant');
          this.microphonePermissionService.openSettings();
        }
      }
      
      throw error;
    }
  }
  
  /**
   * Handle messages from OpenAI
   */
  private handleDataChannelMessage(event: MessageEvent): void {
    const logger = useLogger();
    
    try {
      const msg = JSON.parse(event.data);
      
      // Define message types to skip logging completely
      const ignoredMessageTypes = [
        'response.audio_transcript.delta',
        'response.content_part.added',
        'response.content_part.done',
        'output_audio_buffer.started',
        'output_audio_buffer.stopped',
        'rate_limits.updated',
        'conversation.item.input_audio_transcription.delta',
        'response.audio.done',
        'response.audio_transcript.done'
      ];
      
      // Only log message types that aren't in the ignore list
      if (!ignoredMessageTypes.includes(msg.type)) {
        logger.info('VoiceChatBase: Message received:', msg.type);
      }
      
      // Only log full content for errors or for debugging
      if (msg.type === 'error') {
        logger.error('VoiceChatBase: Error from OpenAI:', JSON.stringify(msg));
      }
      
      // Reset inactivity timer for these specific events only
      const activityEvents = [
        'input_audio_buffer.speech_started',
        'conversation.item.input_audio_transcription.completed',
        'response.audio_transcript.delta',
        'response.created'
      ];
      
      if (activityEvents.includes(msg.type)) {
        // logger.info(`VoiceChatBase: Resetting timer due to activity event: ${msg.type}`);
        this.resetInactivityTimer();
      }
      
      // Handle audio buffer events specially
      if (msg.type === 'output_audio_buffer.started') {
        logger.info('VoiceChatBase: Audio output started');
        // Audio.onplaying should handle this
      } else if (msg.type === 'output_audio_buffer.stopped') {
        logger.info('VoiceChatBase: Audio output stopped');
        // Make sure to set our flag to false
        this.isAudioPlaying.value = false;
      }
      
      // Process the message based on type
      switch (msg.type) {
        case 'input_audio_buffer.speech_started':
          this.handleSpeechStarted();
          break;
          
        case 'input_audio_buffer.committed':
          this.handleAudioCommitted();
          break;
          
        case 'conversation.item.input_audio_transcription.completed':
          this.handleTranscriptionCompleted(msg.transcript || '');
          break;
          
        case 'conversation.item.input_audio_transcription.failed':
          this.handleTranscriptionFailed(msg);
          break;
          
        case 'response.audio_transcript.delta':
          this.handleAssistantDelta(msg.delta);
          break;
          
        case 'conversation.item.created':
          this.handleItemCreated(msg.item);
          break;
          
        case 'response.function_call_arguments.delta':
          this.handleFunctionCallDelta(msg);
          break;
          
        case 'response.function_call_arguments.done':
          this.handleFunctionCallDone(msg);
          break;
          
        case 'output_audio_buffer.started':
          // Reset inactivity timer when assistant starts speaking
          this.resetInactivityTimer();
          break;
          
        case 'output_audio_buffer.stopped':
          // Reset inactivity timer when assistant stops speaking
          // This ensures we have a full timeout period after speech ends
          this.resetInactivityTimer();
          break;
          
        case 'response.done':
          logger.info('VoiceChatBase: OpenAI response completed');
          break;
      }
    } catch (error) {
      logger.error('VoiceChatBase: Error handling data channel message:', error);
    }
  }
  
  /**
   * Handle user speech started event
   */
  private handleSpeechStarted(): void {
    const logger = useLogger();
    logger.info(`VoiceChat_${this.implementationType}: User started speaking`);
    
    this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_USER_SPEECH}_${this.implementationType.toLowerCase()}`);
    this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_USER_SPEECH}_${this.implementationType.toLowerCase()}`);
    
    // Reset inactivity timer when user speaks
    this.resetInactivityTimer();
    
    if (!this.ephemeralUserMessageId.value) {
      this.ephemeralUserMessageId.value = Date.now().toString();
      this.conversation.value.push({
        id: this.ephemeralUserMessageId.value,
        role: 'user',
        text: 'Speaking...',
        timestamp: new Date().toISOString(),
        status: 'speaking',
        isFinal: false
      });
    }
  }
  
  /**
   * Handle audio committed event
   */
  private handleAudioCommitted(): void {
    const logger = useLogger();
    logger.info('VoiceChatBase: Audio committed to OpenAI for processing');
    
    if (this.ephemeralUserMessageId.value) {
      this.updateMessage(this.ephemeralUserMessageId.value, {
        text: 'Processing speech...',
        status: 'processing'
      });
    }
  }
  
  /**
   * Handle transcription completed event
   */
  private handleTranscriptionCompleted(transcript: string): void {
    const logger = useLogger();
    logger.info(`VoiceChat_${this.implementationType}: Final transcription:`, transcript);
    
    const transcriptionData = {
      length: transcript.length
    };
    this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_TRANSCRIPTION_SUCCESS}_${this.implementationType.toLowerCase()}`, transcriptionData);
    this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_TRANSCRIPTION_SUCCESS}_${this.implementationType.toLowerCase()}`, transcriptionData);
    
    if (this.ephemeralUserMessageId.value) {
      this.updateMessage(this.ephemeralUserMessageId.value, {
        text: transcript,
        status: 'complete',
        isFinal: true
      });
      
      // Clear ephemeral ID for next speech
      this.ephemeralUserMessageId.value = null;
    }
  }
  
  /**
   * Handle transcription failed event
   */
  private handleTranscriptionFailed(msg: any): void {
    const logger = useLogger();
    
    const failureData = {
      reason: msg.reason || 'Unknown reason',
      details: msg.details || undefined
    };
    this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_TRANSCRIPTION_FAIL}_${this.implementationType.toLowerCase()}`, failureData);
    this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_TRANSCRIPTION_FAIL}_${this.implementationType.toLowerCase()}`, failureData);
    
    // Update UI to show error
    if (this.ephemeralUserMessageId.value) {
      this.updateMessage(this.ephemeralUserMessageId.value, {
        text: 'Sorry, I couldn\'t understand what you said. Please try again.',
        status: 'error',
        isFinal: true
      });
      this.ephemeralUserMessageId.value = null;
    } else {
      this.conversation.value.push({
        id: Date.now().toString(),
        role: 'assistant',
        text: 'I had trouble processing your speech. Please try again, speaking clearly.',
        timestamp: new Date().toISOString(),
        status: 'error',
        isFinal: true
      });
    }
    
    // Check for 429 rate limit
    if (msg.error && (
      JSON.stringify(msg).includes('429') || 
      JSON.stringify(msg).includes('Too Many Requests')
    )) {
      logger.error('VoiceChatBase: Rate limit (429) detected in transcription failure');
      
      this.conversation.value.push({
        id: Date.now().toString(),
        role: 'assistant',
        text: "You've hit OpenAI's rate limits (Error 429). This usually happens when: \n1. Too many requests in a short time\n2. You've reached your API usage quota\n\nPlease wait a moment before trying again or check your OpenAI account.",
        timestamp: new Date().toISOString(),
        status: 'error',
        isFinal: true
      });
    }
  }
  
  /**
   * Handle assistant response deltas
   */
  private handleAssistantDelta(delta: string): void {
    // Reset inactivity timer when assistant speaks
    this.resetInactivityTimer();
    
    // Find the last assistant message or create a new one
    const lastMsg = this.conversation.value.length > 0 ?
      this.conversation.value[this.conversation.value.length - 1] : null;
    
    if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.isFinal) {
      // Update existing message
      this.updateMessage(lastMsg.id, {
        text: lastMsg.text + delta
      });
    } else {
      // Create new assistant message
      const logger = useLogger();
      logger.info('VoiceChatBase: OpenAI assistant started speaking');
      this.conversation.value.push({
        id: Date.now().toString(),
        role: 'assistant',
        text: delta,
        timestamp: new Date().toISOString(),
        status: 'speaking',
        isFinal: false
      });
    }
  }
  
  /**
   * Handle conversation item created event
   */
  private handleItemCreated(item: any): void {
    const logger = useLogger();
    
    if (!item) return;
    
    logger.info(`VoiceChatBase: New conversation item created: ${item.type || 'unknown'}`);
    
    // Handle function call creation
    if (item.type === 'function_call') {
      const callId = item.call_id;
      const name = item.name;
      const initialArgs = item.arguments || '';
      
      // Store function call in our unified state
      this.trackFunctionCall(callId, {
        name: name,
        arguments: initialArgs,
        status: 'created'
      });
      
      logger.info(`VoiceChatBase: Received function call for ${name} with ID: ${callId}`);
    }
  }
  
  /**
   * Handle function call argument delta
   */
  private handleFunctionCallDelta(msg: any): void {
    const logger = useLogger();
    const callId = msg.call_id;
    if (!callId) return;
    
    const functionCall = this.getFunctionCall(callId);
    
    if (functionCall) {
      // Update the arguments by appending the delta
      this.trackFunctionCall(callId, {
        arguments: functionCall.arguments + (msg.delta || ''),
        status: 'accumulating'
      });
    } else {
      // If no function call exists yet (rare race condition), create one
      this.trackFunctionCall(callId, {
        name: '', // Will be filled in later
        arguments: msg.delta || '',
        status: 'accumulating'
      });
      
      logger.warn(`VoiceChatBase: Received delta for unknown function call ID: ${callId}`);
    }
  }
  
  /**
   * Handle function call arguments done
   */
  private handleFunctionCallDone(msg: any): void {
    const logger = useLogger();
    const callId = msg.call_id;
    
    if (callId && !this.isFunctionCallCompleted(callId)) {
      const functionCall = this.getFunctionCall(callId);
      
      if (functionCall) {
        // Track function call
        const functionData = {
          name: functionCall.name,
          status: functionCall.status
        };
        this.analytics.capture(`${USAGE_EVENTS.VOICE_CHAT_FUNCTION_CALL}_${this.implementationType.toLowerCase()}`, functionData);
        this.firebaseAnalytics.logEvent(`${USAGE_EVENTS.VOICE_CHAT_FUNCTION_CALL}_${this.implementationType.toLowerCase()}`, functionData);
        
        // Mark the arguments as complete
        this.trackFunctionCall(callId, {
          status: 'complete'
        });
        
        logger.info(`VoiceChatBase: All arguments received for function ${functionCall.name || 'unknown'} with ID: ${callId}`);
        logger.debug(`VoiceChatBase: Function call details:`, {
          callId: callId,
          functionName: functionCall.name,
          argsLength: functionCall.arguments.length,
          status: functionCall.status
        });
        
        // Create complete payload and execute
        const payload: FunctionCallPayload = {
          call_id: callId,
          name: functionCall.name,
          arguments: functionCall.arguments
        };
        
        // Only execute if we have a name
        if (functionCall.name) {
          // Mark as executing
          this.trackFunctionCall(callId, {
            status: 'executing'
          });
          
          logger.info(`VoiceChatBase: Executing function ${functionCall.name} with ID: ${callId}`);
          toolRegistry.executeToolCall(payload, this.getToolContext(callId))
            .then(() => {
              // Mark as executed on success
              this.trackFunctionCall(callId, {
                status: 'executed'
              });
            })
            .catch(error => {
              // Mark as error on failure
              this.trackFunctionCall(callId, {
                status: 'error',
                error: error instanceof Error ? error.message : String(error)
              });
              
              logger.error(`VoiceChatBase: Error executing function ${functionCall.name}:`, error);
            });
        } else {
          this.trackFunctionCall(callId, {
            status: 'error',
            error: 'Function name missing'
          });
          
          logger.error(`VoiceChatBase: Unable to execute function call - name not found for call ID: ${callId}`);
        }
      } else {
        logger.error(`VoiceChatBase: Function call completion received for unknown call ID: ${callId}`);
      }
    }
  }
  
  /**
   * Update a message in the conversation
   */
  private updateMessage(id: string, updates: Partial<Message>): void {
    this.conversation.value = this.conversation.value.map(msg => {
      if (msg.id === id) {
        return { ...msg, ...updates };
      }
      return msg;
    });
  }
  
  /**
   * Get tool context for tool execution
   */
  private getToolContext(callId: string = ''): ToolContext {
    return {
      callId,
      dataChannel: this.dataChannel.value,
      completedCalls: new Set(
        Array.from(this.functionCalls.value.entries())
          .filter(([_, call]) => ['executed', 'error'].includes(call.status))
          .map(([id, _]) => id)
      ),
      logger: useLogger(),
      conversation: this.conversation.value
    };
  }
  
  /**
   * Add or update a function call in the state map
   */
  private trackFunctionCall(
    callId: string, 
    updates: Partial<Omit<FunctionCallState, 'id' | 'createdAt'>>
  ): FunctionCallState {
    const existingCall = this.functionCalls.value.get(callId);
    
    const updatedCall: FunctionCallState = existingCall 
      ? { ...existingCall, ...updates }
      : {
          id: callId,
          name: updates.name || '',
          arguments: updates.arguments || '',
          status: updates.status || 'created',
          createdAt: Date.now(),
          ...(updates.result && { result: updates.result }),
          ...(updates.error && { error: updates.error })
        };
    
    this.functionCalls.value.set(callId, updatedCall);
    return updatedCall;
  }
  
  /**
   * Check if a function call has been completed
   */
  private isFunctionCallCompleted(callId: string): boolean {
    const call = this.functionCalls.value.get(callId);
    return !!call && ['executed', 'error'].includes(call.status);
  }
  
  /**
   * Get all information about a function call
   */
  private getFunctionCall(callId: string): FunctionCallState | undefined {
    return this.functionCalls.value.get(callId);
  }
  
  /**
   * Display an error message and log the error
   */
  private showError(message: string, error?: any): void {
    const logger = useLogger();
    const errorMessage = error instanceof Error ? error.message : String(error || '');
    logger.error(`VoiceChatBase: ${message}:`, error);
    
    this.hasError.value = true;
    this.errorMessage.value = errorMessage ? `${message}: ${errorMessage}` : message;
    
    // Clear error after 5 seconds
    setTimeout(() => {
      this.hasError.value = false;
      this.errorMessage.value = '';
    }, 5000);
  }
  
  /**
   * Clean up session-specific resources
   */
  private cleanupSession(): void {
    // Clear session-specific state
    this.functionCalls.value.clear();
    
    // Stop audio activity heartbeat
    this.stopAudioActivityHeartbeat();
    
    // Clean up audio element
    if (this.audioEl) {
      this.audioEl.remove();
      this.audioEl = null;
    }
  }
  
  /**
   * Clean up resources when the component is unmounted
   */
  public cleanup(): void {
    const logger = useLogger();
    logger.info(`VoiceChat_${this.implementationType}: Cleaning up resources`);
    
    // Clear inactivity timer
    this.clearInactivityTimer();
    
    this.stopSession();
    toolRegistry.clearTools();
    logger.info(`VoiceChat_${this.implementationType}: Performed full cleanup including tool registry`);
  }
  
  /**
   * Initiate a graceful shutdown of the chat session
   * This sets the shutting down state and delays the actual stopSession call
   * to allow OpenAI to generate and speak a farewell message
   * 
   * @param reason The reason for shutting down, for logging purposes
   * @param delayMs The delay in milliseconds before stopping the session
   */
  public initiateShutdown(reason: string = 'User requested', delayMs: number = 3000): void {
    const logger = useLogger();
    logger.info(`VoiceChat_${this.implementationType}: Initiating shutdown with reason: ${reason}, delay: ${delayMs}ms`);
    
    // Clear inactivity timer when shutting down
    this.clearInactivityTimer();
    
    // If already shutting down, avoid duplicate shutdown
    if (this.isShuttingDown.value) {
      logger.info(`VoiceChat_${this.implementationType}: Already shutting down, ignoring duplicate request`);
      return;
    }
    
    // If session is not active, no need for delayed shutdown
    if (!this.isSessionActive.value) {
      logger.info('VoiceChatBase: Session not active, no need for shutdown');
      return;
    }
    
    // Set shutting down state
    this.isShuttingDown.value = true;
    this.status.value = 'Ending conversation...';
    
    // Schedule the actual session stop
    setTimeout(() => {
      logger.info('VoiceChatBase: Executing delayed shutdown');
      
      // Double-check that we're still in shutting down state
      if (this.isShuttingDown.value) {
        this._stopSession(true); // Pass true to disable permission reset on Android
      } else {
        logger.info('VoiceChatBase: Shutdown cancelled or already completed');
      }
    }, delayMs);
    
    // Failsafe - if session is still shutting down after delay + 2 seconds, force stop
    setTimeout(() => {
      if (this.isShuttingDown.value) {
        logger.warn('VoiceChatBase: Shutdown taking too long, forcing stop');
        this._stopSession(true);
      }
    }, delayMs + 2000);
  }
  
  /**
   * Abstract method to register tools specific to this voice chat implementation
   * Must be implemented by concrete subclasses
   */
  protected abstract registerTools(): Promise<void>;
  
  /**
   * Abstract method to get the initial prompt instructions for the AI
   * Must be implemented by concrete subclasses
   */
  public abstract getInitialPrompt(): string;
  
  /**
   * Check if the chat is currently in the process of shutting down
   * UI components can use this to show appropriate feedback
   */
  public getShuttingDownState(): boolean {
    return this.isShuttingDown.value;
  }
  
  /**
   * Handle inactivity timeout
   * Adds a farewell message and initiates shutdown
   */
  private handleInactivityTimeout(): void {
    const logger = useLogger();
    // logger.info('VoiceChatBase: Inactivity timeout reached');
    
    // Only proceed if session is active and not already shutting down
    if (!this.isSessionActive.value || this.isShuttingDown.value) {
      return;
    }
    
    // Add inactivity message to the conversation
    this.conversation.value.push({
      id: Date.now().toString(),
      role: 'assistant',
      text: "It seems we've been quiet for a while. I'll end our conversation now. Feel free to start a new chat when you need assistance again.",
      timestamp: new Date().toISOString(),
      status: 'complete',
      isFinal: true
    });
    
    // Initiate shutdown with 15 second delay to allow for the message to be read
    this.initiateShutdown('Inactivity timeout', 15000);
  }
  
  /**
   * Send an inactivity prompt to OpenAI suggesting it should end the conversation
   * OpenAI will then call the stopChat tool which handles the actual shutdown
   */
  private sendInactivityPrompt(): void {
    const logger = useLogger();
    // logger.info('VoiceChatBase: Sending inactivity prompt to OpenAI');
    
    // Only proceed if we have an active session and data channel
    if (!this.isSessionActive.value || this.isShuttingDown.value || !this.dataChannel.value) {
      return;
    }
    
    try {
      // Create a prompt instructing OpenAI to end the conversation due to inactivity
      const inactivityPrompt = {
        type: 'response.create',
        response: {
          instructions: "There has been no conversation for a while. Please end this conversation by calling the stopChat tool with an appropriate farewell message that is not overly long. The user appears to be inactive."
        }
      };
      
      // Send the instruction to OpenAI
      this.dataChannel.value.send(JSON.stringify(inactivityPrompt));
      // logger.info('VoiceChatBase: Inactivity prompt sent to OpenAI');
      
      // Set a backup timer in case OpenAI doesn't call the stopChat tool
      setTimeout(() => {
        if (this.isSessionActive.value && !this.isShuttingDown.value) {
          // logger.warn('VoiceChatBase: OpenAI did not call stopChat after inactivity prompt, forcing shutdown');
          this.handleInactivityTimeout();
        }
      }, 5000); // 5 second backup timeout
    } catch (error) {
      logger.error('VoiceChatBase: Error sending inactivity prompt:', error);
      // Fall back to direct shutdown if sending the prompt fails
      this.handleInactivityTimeout();
    }
  }
  
  /**
   * Start inactivity timer
   * If no activity occurs within the timeout period, the chat will be prompted to end
   */
  private startInactivityTimer(): void {
    const logger = useLogger();
    
    // Clear any existing timer first
    this.clearInactivityTimer();
    
    // Don't start timer if we're shutting down or not active
    if (this.isShuttingDown.value || !this.isSessionActive.value) {
      return;
    }
    
    // logger.info(`VoiceChatBase: Starting inactivity timer (${this.inactivityTimeoutMs}ms)`);
    
    // Create new timer
    this.inactivityTimerId = window.setTimeout(() => {
      // Instead of directly handling timeout, send a prompt to OpenAI
      this.sendInactivityPrompt();
    }, this.inactivityTimeoutMs);
  }
  
  /**
   * Reset inactivity timer
   * Call this whenever there's activity from either user or assistant
   */
  private resetInactivityTimer(): void {
    const logger = useLogger();
    
    // Only reset if session is active and not shutting down
    if (this.isSessionActive.value && !this.isShuttingDown.value) {
      // Add call stack to debug where resets are coming from
      const stack = new Error().stack;
      // logger.debug('VoiceChatBase: Resetting inactivity timer due to activity', { 
      //   source: stack ? stack.split('\n')[2] : 'unknown' 
      // });
      this.startInactivityTimer();
    }
  }
  
  /**
   * Clear inactivity timer
   */
  private clearInactivityTimer(): void {
    if (this.inactivityTimerId !== null) {
      window.clearTimeout(this.inactivityTimerId);
      this.inactivityTimerId = null;
    }
  }
  
  /**
   * Start a heartbeat to reset the inactivity timer while audio is playing
   * This ensures long audio responses don't trigger the timeout
   */
  private startAudioActivityHeartbeat(): void {
    const logger = useLogger();
    this.stopAudioActivityHeartbeat(); // Stop any existing heartbeat
    
    // Basic check that audio should be playing
    if (!this.isAudioPlaying.value || !this.audioEl) {
      // logger.info('VoiceChatBase: Not starting heartbeat - no audio playback detected');
      this.isAudioPlaying.value = false; // Ensure flag is consistent
      return;
    }
    
    // logger.info('VoiceChatBase: Starting audio activity heartbeat');
    
    // Create a heartbeat that fires every 3 seconds
    this.audioActivityHeartbeatId = window.setInterval(() => {
      const now = Date.now();
      const audio = this.audioEl;
      
      // Check for active, progressive audio playback
      if (this.isAudioPlaying.value && 
          audio && 
          !audio.paused && 
          !audio.ended && 
          audio.currentTime > 0) {
        
        // Verify audio position has changed in the last 5 seconds
        const lastPosition = parseFloat(audio.getAttribute('data-previous-position') || '0');
        const hasPositionChanged = Math.abs(audio.currentTime - lastPosition) > 0.1;
        
        if (hasPositionChanged) {
          // Audio is actively playing - update last activity time and position
          // logger.debug(`VoiceChatBase: Audio heartbeat - position changed from ${lastPosition} to ${audio.currentTime.toFixed(2)}`);
          audio.setAttribute('data-previous-position', audio.currentTime.toString());
          this.lastAudioActivityTime = now;
          this.resetInactivityTimer();
        } else {
          // Position hasn't changed - check if we've been in this state too long
          const timeSinceLastActivity = now - this.lastAudioActivityTime;
          
          if (timeSinceLastActivity > 5000) {
            // logger.info(`VoiceChatBase: Audio appears stalled for ${(timeSinceLastActivity/1000).toFixed(1)}s - stopping heartbeat`);
            this.isAudioPlaying.value = false;
            this.stopAudioActivityHeartbeat();
          } else {
            // logger.debug('VoiceChatBase: Audio position unchanged but still within activity window');
          }
        }
      } else {
        // Audio is not playing according to element state
        // logger.info('VoiceChatBase: Audio heartbeat - audio no longer playing');
        this.isAudioPlaying.value = false;
        this.stopAudioActivityHeartbeat();
      }
    }, 3000); // Check every 3 seconds
  }
  
  /**
   * Stop the audio activity heartbeat
   */
  private stopAudioActivityHeartbeat(): void {
    if (this.audioActivityHeartbeatId !== null) {
      window.clearInterval(this.audioActivityHeartbeatId);
      this.audioActivityHeartbeatId = null;
    }
  }
} 